import Phaser from 'phaser';
import { GameScene, type GameSceneConfig } from '../GameScene';
import { Direction } from '@shared/types/entity';
import { TILE_SIZE, MOVEMENT_CONFIG, FOOTSTEP_BASE_DURATION_MS } from '@shared/constants';
import { ClientMessageType } from '@shared/types/messages';
import { DEFAULT_PLAYER_FOOTSTEP_ID } from '../../sprites/FootstepSpriteManager';
import type {
  MovementConfirmedMessage,
  PlayerTeleportedMessage,
  PlayerMapChangeMessage,
} from '@shared/types/messages';

/**
 * Movimiento del player local con client-side prediction: arranca el paso local (startLocalMovement/
 * tryStartLocalMovement leyendo el input), reconcilia con la confirmación del server
 * (handleMovementConfirmed) y aplica teleports / cambios de mapa (handlePlayerTeleported/
 * handlePlayerMapChange). Extraído de GameScene (god-object). El estado (playerObjects, cola de pasos,
 * input) vive en GameScene (host) y se accede vía `this.scene.*`. Ver docs/MOVEMENT_AND_PREDICTION.md.
 */
export class MovementController {
  constructor(private scene: GameScene) {}

  public getNextTile(currentTileX: number, currentTileY: number, direction: Direction): { x: number; y: number } {
    let nextX = currentTileX;
    let nextY = currentTileY;
    
    switch (direction) {
      case Direction.UP: nextY--; break;
      case Direction.DOWN: nextY++; break;
      case Direction.LEFT: nextX--; break;
      case Direction.RIGHT: nextX++; break;
    }
    
    return { x: nextX, y: nextY };
  }

  /**
   * Inicia movimiento inmediato en el cliente (estilo argentumonline.io)
   * 
   * IMPORTANTE: Solo inicia movimiento si NO hay uno en progreso.
   * Esto es exactamente como funciona argentumonline.io:
   * - tratarDeMover() solo mueve si !estaMoviendose()
   * - El InputSystem se encarga de reintentar cada frame
   * 
   * @param direction Dirección del movimiento
   * @returns true si se inició el movimiento, false si no se pudo iniciar
   */
  public startLocalMovement(direction: Direction): boolean {
    if (!this.scene.localSessionId) return false;
    
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render) return false;

    // =====================================================================
    // ESTILO ARGENTUMONLINE.IO: Solo mover si NO hay movimiento NORMAL activo
    // =====================================================================
    // Permitir iniciar movimiento si:
    // - No hay movimiento activo (moveDuration = 0)
    // - O hay una corrección del servidor (moveSpeed = 0)
    // =====================================================================
    const hasNormalMovement = render.moveDuration > 0 && render.moveSpeed > 0;
    
    if (hasNormalMovement) {
      // Hay movimiento normal activo - actualizar dirección visual pero NO iniciar nuevo
      render.visualDirection = direction;
      return false;
    }

    const now = this.scene.time.now;
    
    // Calcular tile actual del cliente (basado en posición visual actual)
    const currentTileX = Math.round(render.currentWorldX / TILE_SIZE - 0.5);
    const currentTileY = Math.round(render.currentWorldY / TILE_SIZE - 0.5);
    
    // Calcular tile siguiente basado en dirección
    const nextTile = this.getNextTile(currentTileX, currentTileY, direction);
    
    // Verificar colisiones client-side
    if (!this.scene.canMoveTo(nextTile.x, nextTile.y)) {
      // Colisión detectada - actualizar dirección pero no moverse
      render.visualDirection = direction;
      return false;
    }

    // =====================================================================
    // INICIAR MOVIMIENTO (estilo argentumonline.io)
    // =====================================================================
    const nextTargetX = nextTile.x * TILE_SIZE + TILE_SIZE / 2;
    const nextTargetY = nextTile.y * TILE_SIZE + TILE_SIZE / 2;
    const effectiveDuration = this.scene.getLocalPlayerMoveDuration();
    
    render.startWorldX = render.currentWorldX;
    render.startWorldY = render.currentWorldY;
    render.targetWorldX = nextTargetX;
    render.targetWorldY = nextTargetY;
    render.moveElapsed = 0; // Reset elapsed time (estilo argentumonline.io)
    render.moveDuration = effectiveDuration;
    render.moveSpeed = GameScene.PLAYER_MOVE_SPEED;
    render.visualDirection = direction;

    // Actualizar tracking de tiles
    render.lastTileX = nextTile.x;
    render.lastTileY = nextTile.y;

    // Huella: marcarla en el tile que se DEJA ATRÁS (origen del paso), orientada
    // según la dirección del movimiento. Solo el jugador local (este método lo es).
    // La duración viene del stat sincronizado del propio jugador. El mod único
    // `noFootsteps` (amuleto Paso Fantasma) suprime la huella por completo.
    // `noFootsteps` suprime el decal VISUAL; `noWalkNoise` suprime el SFX de pasos (ejes separados).
    // spawnFootstepForStep gatea cada uno por su lado, así que se llama siempre.
    const localState = this.scene.room?.state.players.get(this.scene.localSessionId) as unknown as
      | { footstepDuration?: number; noFootsteps?: number; noWalkNoise?: number }
      | undefined;
    const localFsDuration = localState?.footstepDuration ?? FOOTSTEP_BASE_DURATION_MS;
    this.scene.footsteps.spawnFootstepForStep(
      currentTileX,
      currentTileY,
      direction,
      DEFAULT_PLAYER_FOOTSTEP_ID,
      localFsDuration,
      !!localState?.noWalkNoise,
      !!localState?.noFootsteps,
      this.scene.localSessionId ?? ''
    );

    // Mantener viva la animación de walk hasta el fin de este step + una gracia
    // chica, para que el micro-gap hasta que arranque el próximo step (rate-limit)
    // no detenga la anim y la reinicie a col 0. Solo se setea cuando el step
    // EFECTIVAMENTE arranca (caminar bloqueado retorna false antes → idle).
    render.walkUntil = this.scene.time.now + effectiveDuration + GameScene.WALK_ANIM_GRACE_MS;

    return true;
  }

  public tryStartLocalMovement(): void {
    if (!this.scene.localSessionId) return;

    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render) return;

    // Mientras se comercia con un vendedor (común o de hechizos) el server
    // rechaza el movimiento (INTERACTING_NPC → BUSY_NPC). Bloquearlo también
    // client-side: si no, el cliente predice el step y la reconciliación lo
    // rebota al tile donde estaba. No predecir ni enviar MOVE.
    if (this.scene.vendorPanels.isVendorPanelOpen()) return;

    // Verificar si hay movimiento activo (estilo argentumonline.io estaMoviendose())
    const estaMoviendose = render.moveDuration > 0;
    if (estaMoviendose) return; // Ya hay movimiento, no hacer nada
    
    // Verificar si hay tecla presionada (estilo argentumonline.io estaCaminando())
    const direction = this.scene.getDirectionFromInput();
    if (direction === null) return; // No hay tecla, no hacer nada

    const render2 = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render2) return;

    // Provocado (taunt): el server maneja el movimiento (te lleva al taunter). Se
    // ignora el input propio por completo — no predecimos. Ver docs/TAUNT.md.
    if (this.scene.getLocalTaunted()) return;

    // Pérdida de movilidad (server-authoritative): paralizado NO se mueve ni gira;
    // inmovilizado puede girar el heading en su tile pero no avanza. Ver
    // docs/CC_IMMOBILIZE_PARALYZE.md.
    const impair = this.scene.getLocalImpairState();
    if (impair === 2) return; // paralizado: ignorar input por completo
    if (impair === 1) {
      // inmovilizado: solo girar (manda MOVE de heading cuando cambia, sin avanzar).
      if (render2.visualDirection !== direction) {
        render2.visualDirection = direction;
        if (this.scene.colyseusClient?.isConnected() && this.scene.lastSentMoveDir !== direction) {
          this.scene.colyseusClient.send({ type: ClientMessageType.MOVE, direction });
          this.scene.lastSentMoveDir = direction;
        }
      }
      return;
    }

    // Predicción de colisión del tile destino.
    const curTileX = Math.round(render2.currentWorldX / TILE_SIZE - 0.5);
    const curTileY = Math.round(render2.currentWorldY / TILE_SIZE - 0.5);
    const nextTile = this.getNextTile(curTileX, curTileY, direction);
    const blocked = !this.scene.canMoveTo(nextTile.x, nextTile.y);

    if (blocked) {
      // No se puede caminar hacia ese lado: girar el heading (instantáneo, NO
      // gateado por el cooldown de paso) y avisar al server SOLO cuando cambia
      // (sin spamear) para que sincronice el giro a los demás clientes.
      render2.visualDirection = direction;
      if (this.scene.colyseusClient?.isConnected() && this.scene.lastSentMoveDir !== direction) {
        this.scene.colyseusClient.send({ type: ClientMessageType.MOVE, direction });
        this.scene.lastSentMoveDir = direction;
      }
      return;
    }

    // =========================================================================
    // RATE LIMITING del PASO real: el servidor usa Date.now() para verificar
    // que pasaron ~200ms. El cliente usa el mismo gate para no enviar pasos que
    // el servidor rechazaría. (El giro de arriba NO se gatea — girar es instantáneo.)
    // =========================================================================
    const now = performance.now();
    const effectiveDuration = this.scene.getLocalPlayerMoveDuration();
    const elapsed = now - this.scene.lastMoveTimestamp;
    if (elapsed < effectiveDuration) {
      // El PASO está en cooldown, pero el GIRO de heading NO se rate-limitea: si la
      // dirección pedida difiere del facing actual, girar al instante (igual que la rama
      // de tile bloqueado). Sin esto, mirar hacia un tile libre justo después de un paso
      // se sentía "trabado" hasta que expiraba el cooldown.
      if (render2.visualDirection !== direction) {
        render2.visualDirection = direction;
        if (this.scene.colyseusClient?.isConnected() && this.scene.lastSentMoveDir !== direction) {
          this.scene.colyseusClient.send({ type: ClientMessageType.MOVE, direction });
          this.scene.lastSentMoveDir = direction;
        }
      }
      return;
    }

    // Tile libre y fuera de cooldown → iniciar movimiento.
    const started = this.startLocalMovement(direction);
    if (started) {
      this.scene.hideAllHoverLabels();
      if (this.scene.colyseusClient && this.scene.colyseusClient.isConnected()) {
        this.scene.lastMoveTimestamp = now; // Actualizar SOLO cuando enviamos al servidor
        this.scene.colyseusClient.send({ type: ClientMessageType.MOVE, direction });
        this.scene.lastSentMoveDir = direction;
      }
    }
  }

  /**
   * Reconcilia la posición del jugador local con la confirmación del servidor.
   * Si el servidor confirma una posición distinta a la predicha por el cliente,
   * corrige suavemente para evitar desync acumulativo.
   */
  public handleMovementConfirmed(m: MovementConfirmedMessage): void {
    if (!this.scene.localSessionId) return;
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render) return;

    const serverX = m.x;
    const serverY = m.y;

    // Si la posición predicha coincide con la del servidor, no hacer nada
    if (render.lastTileX === serverX && render.lastTileY === serverY) return;

    // Desync detectado: el servidor rechazó el movimiento o lo corrigió
    const dist = Math.abs(render.lastTileX - serverX) + Math.abs(render.lastTileY - serverY);

    if (dist > MOVEMENT_CONFIG.RECONCILE_THRESHOLD_TILES) {
      // Desync grande (teleport/cambio de mapa) - snap instantáneo
      const worldX = serverX * TILE_SIZE + TILE_SIZE / 2;
      const worldY = serverY * TILE_SIZE + TILE_SIZE / 2;
      render.currentWorldX = worldX;
      render.currentWorldY = worldY;
      render.targetWorldX = worldX;
      render.targetWorldY = worldY;
      render.startWorldX = worldX;
      render.startWorldY = worldY;
      render.lastTileX = serverX;
      render.lastTileY = serverY;
      render.moveDuration = 0;
      render.moveSpeed = 0;
      render.moveElapsed = 0;
    } else {
      // Desync menor - corregir el target de interpolación al tile del servidor
      const worldX = serverX * TILE_SIZE + TILE_SIZE / 2;
      const worldY = serverY * TILE_SIZE + TILE_SIZE / 2;
      render.targetWorldX = worldX;
      render.targetWorldY = worldY;
      render.lastTileX = serverX;
      render.lastTileY = serverY;
    }
  }

  /**
   * Translado same-map: snap atómico del entity a (toX,toY).
   * El server ya envió PLAY_ANIMATION por separado, no la reproducimos acá.
   */
  public handlePlayerTeleported(m: PlayerTeleportedMessage): void {
    const worldX = m.toX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = m.toY * TILE_SIZE + TILE_SIZE / 2;

    // Paso forzado de 1 tile (embarque/desembarque): NO snapear — interpolar
    // como un step normal, con los mismos campos que startLocalMovement. El
    // player estaba quieto (moverse cancela el canal), así que no hay step en
    // vuelo que pisar. Ver docs/WATER_AND_BOATS.md.
    if (m.smooth && m.entityId === this.scene.localSessionId) {
      const render = this.scene.playerObjects.get(m.entityId);
      if (render) {
        const effectiveDuration = this.scene.getLocalPlayerMoveDuration();
        render.startWorldX = render.currentWorldX;
        render.startWorldY = render.currentWorldY;
        render.targetWorldX = worldX;
        render.targetWorldY = worldY;
        render.moveElapsed = 0;
        render.moveDuration = effectiveDuration;
        render.moveSpeed = GameScene.PLAYER_MOVE_SPEED;
        render.lastTileX = m.toX;
        render.lastTileY = m.toY;
        if (m.direction !== undefined) {
          render.visualDirection = m.direction;
        }
        render.walkUntil = this.scene.time.now + effectiveDuration + GameScene.WALK_ANIM_GRACE_MS;
      }
      // Mismo gate que un step enviado: que el próximo move respete el cooldown.
      this.scene.lastMoveTimestamp = performance.now();
      return;
    }

    // Local player: reset COMPLETO de interpolación + rate limit del move.
    if (m.entityId === this.scene.localSessionId) {
      const render = this.scene.playerObjects.get(m.entityId);
      if (render) {
        render.currentWorldX = worldX;
        render.currentWorldY = worldY;
        render.startWorldX = worldX;
        render.startWorldY = worldY;
        render.targetWorldX = worldX;
        render.targetWorldY = worldY;
        render.lastTileX = m.toX;
        render.lastTileY = m.toY;
        render.moveDuration = 0;
        render.moveSpeed = 0;
        render.moveElapsed = 0;
        // Cortar la gracia de animación de walk: tras un teleport queremos
        // re-evaluar idle/walk de inmediato, sin cola de caminata.
        render.walkUntil = 0;
        if (m.direction !== undefined) {
          render.visualDirection = m.direction;
        }
      }
      // Rate-limit del move: usar el mismo timestamp que tryStartLocalMovement consulta
      this.scene.lastMoveTimestamp = performance.now();
      // Snap inmediato de la cámara para evitar barrido visible
      this.scene.cameraCtl.updateCameraPosition();
      return;
    }

    // Remote player: limpiar stepQueue y snap visual.
    const remote = this.scene.playerObjects.get(m.entityId);
    if (remote) {
      remote.stepQueue = [];
      remote.currentWorldX = worldX;
      remote.currentWorldY = worldY;
      remote.startWorldX = worldX;
      remote.startWorldY = worldY;
      remote.targetWorldX = worldX;
      remote.targetWorldY = worldY;
      remote.lastTileX = m.toX;
      remote.lastTileY = m.toY;
      remote.moveDuration = 0;
      remote.moveSpeed = 0;
      remote.moveElapsed = 0;
    }
  }

  /**
   * Translado cross-map: reiniciar la escena con el nuevo mapId.
   * El server ya persistió destMapId/destX/destY en DB; al rejoin, onJoin
   * cargará la posición correcta. La animación de llegada la dispara la
   * nueva room al final de onJoin (delay 500ms).
   */
  public handlePlayerMapChange(m: PlayerMapChangeMessage): void {
    console.log(`[GameScene] PLAYER_MAP_CHANGE → map ${m.destMapId} at (${m.destX},${m.destY})`);
    this.scene.scene.restart({
      serverUrl: this.scene.configData.serverUrl,
      characterId: this.scene.configData.characterId,
      mapId: m.destMapId,
      arrivalAnimationId: m.arrivalAnimationId,
      randomizeArrival: m.randomizeArrival ?? false,
      // Translado: NO debe otorgar protección de aparición (solo login).
      isMapChange: true,
    } satisfies GameSceneConfig);
  }

}
